import { useCallback, useEffect, useState } from "react";

/**
 * Per-user column preferences for the Players table.
 * Stored in localStorage so admins don't have to re-toggle each visit.
 *
 * Schema: { order: string[], hidden: string[] }
 *   order  — column keys in display order
 *   hidden — keys the user has chosen to hide
 *
 * Locked columns (Name, Actions) are always rendered regardless of `hidden`.
 */

// Bumped to v2 when the Email column was added — a saved v1 order has no
// "Email" key, so without the bump existing admins would never see the new
// column until they reset their view.
const STORAGE_KEY = "gmb.players.columnPrefs.v2";

// Labels match the existing PlayersTable headers exactly so UI copy stays consistent.
export const DEFAULT_COLUMNS = [
  { key: "Name", label: "Players Name", locked: true },
  { key: "Email", label: "Email" },
  { key: "Workspace", label: "Workspace Assigned" },
  { key: "Team", label: "Team" },
  { key: "Phone", label: "Phone", defaultHidden: true },
  { key: "Status", label: "Status" },
  { key: "Permission", label: "Players Role" },
  { key: "Actions", label: "Actions", locked: true },
];

const readPrefs = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch {
    return null;
  }
};

const writePrefs = (prefs) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    /* ignore */
  }
};

const seedDefaults = () => ({
  order: DEFAULT_COLUMNS.map((c) => c.key),
  hidden: DEFAULT_COLUMNS.filter((c) => c.defaultHidden).map((c) => c.key),
});

export function useColumnPreferences() {
  const [prefs, setPrefs] = useState(() => readPrefs() || seedDefaults());

  useEffect(() => {
    writePrefs(prefs);
  }, [prefs]);

  const isVisible = useCallback(
    (key) => {
      const meta = DEFAULT_COLUMNS.find((c) => c.key === key);
      if (meta?.locked) return true;
      return !prefs.hidden.includes(key);
    },
    [prefs.hidden],
  );

  const toggleVisible = useCallback((key) => {
    setPrefs((p) => {
      const meta = DEFAULT_COLUMNS.find((c) => c.key === key);
      if (meta?.locked) return p;
      const isHidden = p.hidden.includes(key);
      return {
        ...p,
        hidden: isHidden ? p.hidden.filter((k) => k !== key) : [...p.hidden, key],
      };
    });
  }, []);

  const reorder = useCallback((fromKey, toKey) => {
    if (fromKey === toKey) return;
    setPrefs((p) => {
      const next = [...p.order];
      const fromIdx = next.indexOf(fromKey);
      const toIdx = next.indexOf(toKey);
      if (fromIdx < 0 || toIdx < 0) return p;
      next.splice(fromIdx, 1);
      next.splice(toIdx, 0, fromKey);
      return { ...p, order: next };
    });
  }, []);

  const reset = useCallback(() => setPrefs(seedDefaults()), []);

  // Resolved column list — ordered, with `visible` decoration
  const columns = prefs.order
    .map((key) => DEFAULT_COLUMNS.find((c) => c.key === key))
    .filter(Boolean)
    .map((c) => ({ ...c, visible: isVisible(c.key) }));

  return { columns, isVisible, toggleVisible, reorder, reset };
}
